import { Suspense, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { MantineProvider, ColorScheme, ColorSchemeProvider } from '@mantine/core'
import { useHotkeys, useLocalStorage } from '@mantine/hooks'
import { Notifications } from '@mantine/notifications'
import { ErrorProvider } from 'context'
import { AuthProvider, ModalsProvider, FeatureToggleProvider } from 'context'
import { OfflineBanner } from 'components/OfflineBanner'
import { ArcDocumentBranding } from 'components/ArcDocumentBranding'
import { useArcBranding } from 'hooks'
import { Routes } from './routes'
import Theme from './themes'
import MosaicoGlobalStyles from './themes/MosaicoGlobalStyles'
import { MosaicoTheme } from './themes/MosaicoTheme'
import { arcThemeOverride } from './themes/ArcTheme'
import { installArcSpeculationRules, removeArcSpeculationRules } from './utils/speculationRules'
import connectOpenReplayHook from './hooks/connectOpenReplayHook'
import { startProjectsScreenMonitoring } from 'helpers/bugsnag'

// Must start before the first render so API failures during the projects
// screen's initial load are already captured.
startProjectsScreenMonitoring()

import './i18n'
import './@legacy/assets/stylesheets/bootstrap-v4.0.0.css'
import './app.css'
/* Arc-only rules (html.arc-branding) — must stay right after app.css so the
   cascade order matches when these rules lived at the end of app.css */
import './themes/arc-branding.css'
import './@legacy/assets/stylesheets/all.scss'
import useCurrentUser from '@legacy/common/hooks/current-user-hook'

const queryClient = new QueryClient()

const App = () => {
  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  })
  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'))
  useHotkeys([['mod+J', () => toggleColorScheme()]])

  const currentUrl = window.location.pathname
  const currentUser = useCurrentUser()

  // Arc branding (AR-797): cached flag value first, then the Flipper fetch
  const isArcBrandingEnabled = useArcBranding()
  const legacyTheme = { colorScheme, ...MosaicoTheme(colorScheme) }
  const mantineTheme = isArcBrandingEnabled ? arcThemeOverride(legacyTheme) : legacyTheme

  // Arc-only: speculatively prerender monolith (Rails) destinations on link
  // hover so leaving the SPA feels instant. Silent no-op without browser
  // support; zero effect while the arc flag is off (see utils/speculationRules
  // for the allowlist and the documented resource-cost trade-offs)
  useEffect(() => {
    if (!isArcBrandingEnabled) return
    installArcSpeculationRules()
    return () => removeArcSpeculationRules()
  }, [isArcBrandingEnabled])

  useEffect(() => {
    if (!currentUrl.split('/').includes('signin') && !currentUrl.split('/').includes('signout')) {
      connectOpenReplayHook(currentUser?.data)
    }
  }, [currentUser])

  return (
    <>
      <OfflineBanner />
      <Suspense fallback={<div>Loading...</div>}>
        <BrowserRouter>
          <>
            <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
              <MantineProvider
                theme={mantineTheme}
                withCSSVariables
                withGlobalStyles
                withNormalizeCSS
              >
                <ModalsProvider>
                  <MosaicoGlobalStyles />
                  <Notifications position='top-right' zIndex={110000} />
                  <Theme>
                    <QueryClientProvider client={queryClient}>
                      <AuthProvider>
                        <ErrorProvider>
                          <FeatureToggleProvider>
                            <ArcDocumentBranding />
                            <Routes />
                          </FeatureToggleProvider>
                        </ErrorProvider>
                      </AuthProvider>
                    </QueryClientProvider>
                  </Theme>
                </ModalsProvider>
              </MantineProvider>
            </ColorSchemeProvider>
          </>
        </BrowserRouter>
      </Suspense>
    </>
  )
}

const container = document.getElementById('root')

if (container) {
  const root = createRoot(container as HTMLElement)
  root.render(<App />)
} else {
  console.error('Elemento com id "root" não encontrado')
  location.reload()
}
